<script lang="ts" setup>
import { computed, watch, onMounted, useSlots } from "vue";
import type { IIconProps } from "./VIcon.types";
import useLoadIcon from "@magnit/icons/src/composables/useLoadIcon";

const props = withDefaults(defineProps<IIconProps>(), {
  project: "base",
});
const slots = useSlots();

const asyncIcon = computed(() => Boolean(!slots.default && props.name));

const iconClassName = computed(() => {
  if (!asyncIcon.value && !props.color) return ["pl-icon_primary"];
  else if (!asyncIcon.value || (asyncIcon.value && props.color))
    return [`pl-icon_${props.color}`];
  return [];
});
const style = computed(() => {
  const commonStyle = { "--_r": props.rotate };
  if (!asyncIcon.value || !props.name) return commonStyle;
  const size = props.size || props.name.split("-")[0];
  return {
    ...commonStyle,
    width: `${size}px`,
    height: `${size}px`,
  };
});

const figureHTML = computed(() => loadedIcon.value ?? "");

const { fetchAsyncIcon, loadedIcon } = useLoadIcon(
  props.name,
  props.project,
  asyncIcon,
);

onMounted(fetchAsyncIcon);
watch(() => props.name, fetchAsyncIcon);
</script>

<template>
  <div
    v-if="$slots.default"
    v-bind="$attrs"
    class="pl-icon"
    :class="iconClassName"
    :style="style"
  >
    <slot />
  </div>
  <div
    v-else
    v-bind="$attrs"
    class="pl-icon pl-icon-async"
    :class="iconClassName"
    :style="style"
    v-html="figureHTML"
  />
</template>

<style lang="postcss" scoped>
.pl-icon {
  --_r: 0deg;
  --pl-icon-rotate: var(--_r);

  display: inline-flex;
  align-items: center;
  transform: rotate(var(--pl-icon-rotate));

  &_primary {
    color: var(--pl-icon-primary);

    &.pl-icon-async :deep(svg path) {
      fill: var(--pl-icon-primary) !important;
    }
  }

  &_secondary {
    color: var(--pl-icon-secondary);

    &.pl-icon-async :deep(svg path) {
      fill: var(--pl-icon-secondary) !important;
    }
  }

  &_tertiary {
    color: var(--pl-icon-tertiary);

    &.pl-icon-async :deep(svg path) {
      fill: var(--pl-icon-tertiary) !important;
    }
  }

  &_muted {
    color: var(--pl-icon-muted);

    &.pl-icon-async :deep(svg path) {
      fill: var(--pl-icon-muted) !important;
    }
  }

  &_invert-primary {
    color: var(--pl-icon-invert-primary);

    &.pl-icon-async :deep(svg path) {
      fill: var(--pl-icon-invert-primary) !important;
    }
  }

  &_invert-secondary {
    color: var(--pl-icon-invert-secondary);

    &.pl-icon-async :deep(svg path) {
      fill: var(--pl-icon-invert-secondary) !important;
    }
  }

  &_invert-tertiary {
    color: var(--pl-icon-invert-tertiary);

    &.pl-icon-async :deep(svg path) {
      fill: var(--pl-icon-invert-tertiary) !important;
    }
  }

  &_success {
    color: var(--pl-icon-system-success);

    &.pl-icon-async :deep(svg path) {
      fill: var(--pl-icon-system-success) !important;
    }
  }

  &_success-weak {
    color: var(--pl-icon-system-success-weak);

    &.pl-icon-async :deep(svg path) {
      fill: var(--pl-icon-system-success-weak) !important;
    }
  }

  &_warning {
    color: var(--pl-icon-system-warning);

    &.pl-icon-async :deep(svg path) {
      fill: var(--pl-icon-system-warning) !important;
    }
  }

  &_warning-weak {
    color: var(--pl-icon-system-warning-weak);

    &.pl-icon-async :deep(svg path) {
      fill: var(--pl-icon-system-warning-weak) !important;
    }
  }

  &_error {
    color: var(--pl-icon-system-error);

    &.pl-icon-async :deep(svg path) {
      fill: var(--pl-icon-system-error) !important;
    }
  }

  &_error-weak {
    color: var(--pl-icon-system-error-weak);

    &.pl-icon-async :deep(svg path) {
      fill: var(--pl-icon-system-error-weak) !important;
    }
  }

  &_project {
    color: var(--pl-icon-project);

    &.pl-icon-async :deep(svg path) {
      fill: var(--pl-icon-project) !important;
    }
  }

  &_project-weak {
    color: var(--pl-icon-project-weak);

    &.pl-icon-async :deep(svg path) {
      fill: var(--pl-icon-project-weak) !important;
    }
  }
}
</style>
